import React from 'react';
import { Calendar, AlertTriangle, Clock, DollarSign, CheckCircle, X, Mail, Phone, MapPin } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import Seo from './Seo';

export default function CancellationPolicy() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-cyan-50">
      <Seo title="Cancellation Policy" path="/cancellation-policy" description="Bubbles & Bark's 24-hour cancellation policy, fees and how to reschedule." />
      <Header showContactInfo={true} showLoginButton={true} />
      
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <Calendar className="w-8 h-8 text-orange-600" />
            <h1 className="text-4xl font-bold text-gray-800">Cancellation Policy</h1>
          </div>
          <p className="text-lg text-gray-600">
            Understanding our cancellation, no-show, and late pickup policies
          </p>
          <p className="text-sm text-gray-500 mt-2">Last updated: January 2025</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-3xl shadow-xl p-8 space-y-8">
          
          {/* Fee Overview */}
          <section className="bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-200 rounded-2xl p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
              <AlertTriangle className="w-6 h-6 text-red-600 mr-3" />
              Policy Fee Overview
            </h2>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl p-4 text-center shadow-md">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <X className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="font-bold text-red-800 mb-2">Late Cancellation</h3>
                <p className="text-2xl font-bold text-red-600 mb-1">$40</p>
                <p className="text-sm text-gray-600">Less than 24 hours notice</p>
              </div>
              
              <div className="bg-white rounded-xl p-4 text-center shadow-md">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <AlertTriangle className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="font-bold text-orange-800 mb-2">No-Show</h3>
                <p className="text-2xl font-bold text-orange-600 mb-1">$40</p>
                <p className="text-sm text-gray-600">Pet not available at appointment</p>
              </div>
              
              <div className="bg-white rounded-xl p-4 text-center shadow-md">
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
                <h3 className="font-bold text-yellow-800 mb-2">Late Pickup</h3>
                <p className="text-2xl font-bold text-yellow-600 mb-1">$40</p>
                <p className="text-sm text-gray-600">Over 1 hour after ready call/text</p>
              </div>
            </div>
          </section>

          {/* Free Cancellation */}
          <section>
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              Free Cancellation & Rescheduling
            </h2>
            <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-6">
              <div className="space-y-4 text-gray-700">
                <h3 className="text-lg font-semibold text-green-800 flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                  No Fees When You Give 24+ Hours Notice
                </h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Cancel appointments 24 hours or more in advance - completely free</li>
                  <li>Reschedule to any available time slot without penalty</li>
                  <li>Change service types or add services before your appointment</li>
                  <li>Modify pet information or special instructions</li>
                </ul>
                
                <h3 className="text-lg font-semibold text-green-800 flex items-center mt-6">
                  <Clock className="w-5 h-5 text-green-600 mr-2" />
                  How to Cancel or Reschedule
                </h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Log into your account and modify your booking online</li>
                  <li>Call us at (407) 868-8608 during business hours</li>
                  <li>Send an email to info@bubblesbark.com</li>
                  <li>Text us if you have our mobile contact information</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Late Cancellation Policy */}
          <section>
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
              <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mr-3">
                <X className="w-5 h-5 text-red-600" />
              </div>
              Late Cancellation Policy ($40 Fee)
            </h2>
            <div className="space-y-4 text-gray-700">
              <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
                <p className="font-semibold text-red-800 mb-2">$40 Fee Applied For:</p>
                <ul className="space-y-1 text-red-700">
                  <li>• Cancelling with less than 24 hours notice</li>
                  <li>• Same-day cancellations for any reason</li>
                  <li>• Cancelling within 2 hours of your appointment time</li>
                </ul>
              </div>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-6">Why This Policy Exists</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Our groomers block out time specifically for your pet</li>
                <li>Last-minute cancellations prevent us from booking other clients</li>
                <li>We may have already prepared supplies and workspace for your visit</li>
                <li>This policy ensures fair scheduling for all our customers</li>
              </ul>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-6">Emergency Exceptions</h3>
              <p>
                We understand that true emergencies happen. In cases of pet illness, family emergency, 
                or severe weather, we will waive the late cancellation fee on a case-by-case basis. 
                Please contact us as soon as possible to discuss your situation.
              </p>
            </div>
          </section>

          {/* No-Show Policy */}
          <section>
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
              <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center mr-3">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
              </div>
              No-Show Policy ($40 Fee)
            </h2>
            <div className="space-y-4 text-gray-700">
              <div className="bg-orange-50 border-l-4 border-orange-400 p-4 rounded">
                <p className="font-semibold text-orange-800 mb-2">$40 Fee Applied For:</p>
                <ul className="space-y-1 text-orange-700">
                  <li>• No-show without cancelling</li>
                </ul>
              </div>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-6">Avoiding No-Show Fees</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Arrive 5-10 minutes early for your appointment</li>
                <li>Set reminders on your phone or calendar</li>
                <li>Confirm your appointment the day before</li>
                <li>Call immediately if you're running late</li>
                <li>Ensure your pet is ready and available at appointment time</li>
              </ul>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-6">Late Arrival Grace Period</h3>
              <p>
                We offer a 15-minute grace period for late arrivals. If you arrive 16-30 minutes late, 
                we may need to shorten your service or reschedule, but no fee will be charged if you 
                contacted us. After 30 minutes, the no-show policy applies.
              </p>
            </div>
          </section>

          {/* Late Pickup Policy */}
          <section>
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
              <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center mr-3">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              Late Pickup Policy ($40 Fee)
            </h2>
            <div className="space-y-4 text-gray-700">
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                <p className="font-semibold text-yellow-800 mb-2">$40 Fee Applied For:</p>
                <ul className="space-y-1 text-yellow-700">
                  <li>• Late pickup (over 1 hour after ready call/text)</li>
                  <li>• Cancellation within 24 hours of appointment</li>
                  <li>• No-show without cancelling</li>
                </ul>
                <p className="text-sm text-yellow-700 mt-3 font-medium">Please communicate with us to avoid these charges</p>
              </div>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-6">Pickup Timeline</h3>
              <div className="grid md:grid-cols-3 gap-4 mt-4">
                <div className="bg-green-100 rounded-lg p-4 text-center">
                  <Clock className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <h4 className="font-semibold text-green-800">0-30 Minutes</h4>
                  <p className="text-sm text-green-700">Ideal pickup window</p>
                </div>
                <div className="bg-yellow-100 rounded-lg p-4 text-center">
                  <Clock className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                  <h4 className="font-semibold text-yellow-800">30-60 Minutes</h4>
                  <p className="text-sm text-yellow-700">Grace period - no fee</p>
                </div>
                <div className="bg-red-100 rounded-lg p-4 text-center">
                  <DollarSign className="w-8 h-8 text-red-600 mx-auto mb-2" />
                  <h4 className="font-semibold text-red-800">60+ Minutes</h4>
                  <p className="text-sm text-red-700">$40 late pickup fee</p>
                </div>
              </div>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-6">Pickup Notifications</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>We'll call/text when your pet's service is complete</li>
                <li>Please respond promptly to confirm your pickup time</li>
                <li>Let us know if you need to delay pickup</li>
                <li>We can hold your pet for up to 1 hour at no charge</li>
              </ul>
            </div>
          </section>

          {/* Payment of Fees */}
          <section>
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                <DollarSign className="w-5 h-5 text-blue-600" />
              </div>
              Fee Payment & Disputes
            </h2>
            <div className="space-y-4 text-gray-700">
              <h3 className="text-lg font-semibold text-gray-800">How Fees Are Charged</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Fees are automatically charged to your payment method on file</li>
                <li>You'll receive an email receipt and explanation of the charge</li>
                <li>Fees must be paid before booking future appointments</li>
                <li>Payment plans may be available for financial hardship cases</li>
              </ul>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-6">Disputing Fees</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Contact us within 7 days if you believe a fee was charged in error</li>
                <li>Provide documentation or explanation of the circumstances</li>
                <li>We'll review each case individually and respond within 3 business days</li>
                <li>Valid disputes will result in immediate fee refunds</li>
              </ul>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-6">Repeat Violations</h3>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800">
                  <strong>Important:</strong> Customers with 3 or more policy violations within 6 months 
                  may be required to pay in advance for future appointments or may have their account 
                  suspended at our discretion.
                </p>
              </div>
            </div>
          </section>

          {/* Contact Information */}
          <section className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-2xl p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
              <Mail className="w-6 h-6 text-blue-600 mr-3" />
              Questions About Our Cancellation Policy?
            </h2>
            <div className="space-y-4 text-gray-700">
              <p>We're here to help! Contact us if you have questions or need to discuss your situation:</p>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Phone className="w-5 h-5 text-green-600" />
                    <div>
                      <span className="font-semibold">(407) 868-8608</span>
                      <p className="text-sm text-gray-600">Call during business hours</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Mail className="w-5 h-5 text-blue-600" />
                    <div>
                      <span className="font-semibold">info@bubblesbark.com</span>
                      <p className="text-sm text-gray-600">Email anytime</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <MapPin className="w-5 h-5 text-red-600" />
                    <div>
                      <span className="font-semibold">12188 Lake Underhill Rd</span>
                      <p className="text-sm text-gray-600">Orlando, FL 32825</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Clock className="w-5 h-5 text-purple-600" />
                    <div>
                      <span className="font-semibold">Mon-Fri: 9AM-5PM</span>
                      <p className="text-sm text-gray-600">Sat: 9AM-6PM, Sun: 10AM-3PM</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg p-4 mt-6">
                <p className="text-center text-gray-600">
                  <strong>Remember:</strong> We want to work with you! If you're having trouble with our 
                  policies or have special circumstances, please reach out. We're often able to find 
                  solutions that work for everyone.
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}